import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type NavigationLocation = {
  pathname: string;
  state: unknown;
};

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

type NavigationContextValue = {
  location: NavigationLocation;
  navigate: (to: string, options?: NavigateOptions) => void;
};

const configuredBasePath = import.meta.env.BASE_URL.replace(/\/+$/, "");
const basePath = configuredBasePath === "/" ? "" : configuredBasePath;
const NavigationContext = createContext<NavigationContextValue | null>(null);

function readLocation(): NavigationLocation {
  const browserPath = window.location.pathname;
  const pathname = basePath && browserPath.startsWith(`${basePath}/`)
    ? browserPath.slice(basePath.length)
    : browserPath === basePath
      ? "/"
      : browserPath;

  return {
    pathname: pathname.replace(/\/+$/, "") || "/",
    state: window.history.state,
  };
}

function makeBrowserPath(to: string) {
  if (/^(?:[a-z]+:)?\/\//i.test(to)) return to;
  const normalizedPath = to.startsWith("/") ? to : `/${to}`;
  return `${basePath}${normalizedPath}` || "/";
}

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useState<NavigationLocation>(readLocation);

  useEffect(() => {
    const handlePopState = () => setLocation(readLocation());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((to: string, options?: NavigateOptions) => {
    const browserPath = makeBrowserPath(to);
    if (options?.replace) {
      window.history.replaceState(options.state ?? null, "", browserPath);
    } else {
      window.history.pushState(options?.state ?? null, "", browserPath);
    }
    setLocation(readLocation());
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) throw new Error("NavigationProvider no está disponible");
  return context;
}

export function useNavigate() {
  return useNavigation().navigate;
}

export function useLocation() {
  return useNavigation().location;
}

export function useParams() {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/cronica\/([^/]+)$/);
  return { slug: match ? decodeURIComponent(match[1]) : undefined };
}

type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
};

export function Link({ to, onClick, target, ...props }: LinkProps) {
  const navigate = useNavigate();
  const href = makeBrowserPath(to);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && target !== "_self") ||
      props.download !== undefined
    ) {
      return;
    }

    event.preventDefault();
    navigate(to);
  };

  return <a {...props} href={href} target={target} onClick={handleClick} />;
}
